import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  CalendarToday,
  CheckCircle,
  ChevronRight,
} from '@mui/icons-material';
import { WorkoutSession } from '../types/workout';

/**
 * WeeklyHistoryItem Component
 * 
 * A reusable component for displaying weekly workout history in a list.
 * Features:
 * - Week number and completion status
 * - Progress indicator showing completed workouts
 * - Click navigation to detailed view
 * - Hover effects
 * - Consistent styling with gradient icon
 */

export interface WeeklyHistoryItemProps {
  /** The week number */
  weekNumber: number;
  /** Array of workout sessions for this week */
  weekSessions: WorkoutSession[];
  /** Whether the week is fully completed */
  isCompleted: boolean;
  /** Callback function when the item is clicked */
  onClick: () => void;
}

export const WeeklyHistoryItem: React.FC<WeeklyHistoryItemProps> = ({
  weekNumber,
  weekSessions,
  isCompleted,
  onClick
}) => {
  // Calculate completion stats
  const totalWorkoutDays = 5; // Monday, Tuesday, Thursday, Friday, Saturday
  const completedWorkouts = weekSessions.filter(session => session.completed).length;
  const completionPercentage = Math.round((completedWorkouts / totalWorkoutDays) * 100);
  
  // Check if it's a ramp up week
  const isRampUpWeek = [1, 2, 6, 7].includes(weekNumber);

  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        }
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              icon={<CalendarToday />}
              label=""
              sx={{ 
                width: 56, 
                height: 56,
                background: isCompleted 
                  ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '& .MuiChip-label': { display: 'none' },
                '& .MuiChip-icon': { fontSize: 28, margin: 0, color: 'white' }
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body1" component="h3" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                  Week {weekNumber}
                </Typography>
                {isRampUpWeek && (
                  <Chip 
                    label="Ramp Up" 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText'
                    }} 
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {completedWorkouts}/{totalWorkoutDays} workouts completed
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={completionPercentage} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: isCompleted ? 'success.main' : 'primary.main',
                  }
                }} 
              />
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isCompleted && (
              <CheckCircle color="success" sx={{ fontSize: 32 }} />
            )}
            <IconButton>
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}; 